import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Turno, turnosConstants, TurnosFilter } from '../types/turnos.type';
import { useAuth } from '../../auth/AuthContext';
import { Role } from '../types/medico.types';

const entityConstants = turnosConstants

const fetchMyTurnosPaciente = async (filter?: TurnosFilter): Promise<Turno[]> => {
	const response = await api.get(`${entityConstants.apiEndpoint}/myturnos/paciente/${filter || ''}`)
	return response?.data
}

const fetchMyTurnosMedico = async (filter?: TurnosFilter): Promise<Turno[]> => {
	const response = await api.get(`${entityConstants.apiEndpoint}/myturnos/medico/${filter || ''}`)
	return response?.data
}

export const useMyTurnosPaciente = (filter?: TurnosFilter) => {
	const queryClient = useQueryClient()
	const query = useQuery<Turno[], Error>({
		queryKey: [`myTurnos${filter || ''}`],
		queryFn: () => fetchMyTurnosPaciente(filter),
		enabled: true
	})

	// Función para invalidar y refrescar los turnos del paciente
	const invalidateMyTurnosPaciente = async () => {
		try {

			// Invalidar todas las queries de turnos del paciente que empiecen con 'myTurnos'
			await queryClient.invalidateQueries({
				predicate: (query) => {
					const queryKey = query.queryKey[0] as string
					return typeof queryKey === 'string' && queryKey.startsWith('myTurnos')
				}
			})
			
		} catch (error) {
			console.error('❌ Error al invalidar turnos del paciente:', error)
		}
	}

	return {
		...query,
		invalidateMyTurnosPaciente
	}
}

export const useMyTurnosMedico = (filter?: TurnosFilter) => {
	return useQuery<Turno[], Error>({
		queryKey: [`myTurnos${filter || ''}`],
		queryFn: () => fetchMyTurnosMedico(filter),
		enabled: true
	})
}

const fetchAllTurnos = async (): Promise<Turno[]> => {
	const response = await api.get(`${entityConstants.apiEndpoint}/all`)
	return response?.data
}

export const useAllTurnos = () => {
	const { role} = useAuth()

	return useQuery<Turno[], Error>({
		queryKey: [`myTurnos`],
		queryFn: () => role === Role.MEDICO ? fetchMyTurnosMedico() : fetchAllTurnos(),
		enabled: true
	})
}

const fetchTurnos = async (fechaDesde: string, fechaHasta: string): Promise<Turno[]> => {
	const response = await api.get(`/api/turnos/med/${fechaDesde}/${fechaHasta}`)
	return response.data
}

export const useTurnosMedico = (fechaDesde: string, fechaHasta: string) => {
	return useQuery<Turno[], Error>({
		queryKey: ['turnos', fechaDesde, fechaHasta],
		queryFn: () => fetchTurnos(fechaDesde, fechaHasta),
		enabled: fechaDesde !== undefined && fechaDesde !== '' && fechaHasta !== undefined && fechaHasta !== ''
	})
}

const fetchTurnoDelMedico = async (id: number): Promise<Turno> => {
	const response = await api.get(`/api/turnos/medicoById/${id}`)
	return response.data
}
const fetchTurnoDelAdmin = async (id: number): Promise<Turno> => {
	const response = await api.get(`/api/turnos/adminById/${id}`)
	return response.data
}

export const useTurnoMedico = (id: number) => {
	const { role } = useAuth()

	return useQuery<Turno, Error>({
		queryKey: ['turno', id],
		queryFn: () => role === Role.MEDICO ? fetchTurnoDelMedico(id) : fetchTurnoDelAdmin(id),
		enabled: id !== undefined,
	})
}

const fetchTurnoDelPaciente = async (id: number): Promise<Turno> => {
	const response = await api.get(`/api/turnos/pacienteById/${id}`)
	return response.data
}

export const useTurnoDelPaciente = (id?: number | null) => {
	const isValidId = typeof id === 'number' && Number.isFinite(id) && id > 0

	return useQuery<Turno, Error>({
		queryKey: ['turno-paciente', isValidId ? id : null],
		queryFn: () => fetchTurnoDelPaciente(id as number),
		enabled: isValidId,
	})
}